import { DeckAPI, ModalButton, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { Inject } from '@odinjs/odin';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import BaseController from '../../BaseController';
import { MessageService,     } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalButtonType, ModalType, Placement, ToastLevel } from '@philips-internal/oswald-ui-constants';

import './selected-pending-exams-controller.css';
import { ListPendingParams, SelectedBarcodeExam, submitSelectedExamListFn } from '../../../interfaces/exam-collect/pending-exam-collection';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { FragmentNames, SelectedPendingExamsAliases } from '../../../codegen';

export const PendingExamsModalId = 'pending-exams-modal';

@Controller({ component: SelectedPendingExamsAliases.SELECTED_PENDING_EXAMS_PANEL, fragment: FragmentNames.SELECTED_PENDING_EXAMS })
class SelectedPendingExamsController extends BaseController {

  @Inject(SelectedPendingExamsAliases.SELECTED_PENDING_EXAMS_DECK)
  private deckApi!: DeckAPI;

  groupData(data: SelectedBarcodeExam[]): Record<string, ListPendingParams[]> {
    return data.reduce<Record<string, ListPendingParams[]>>((accumulator, value) => {
      const groupByFieldValue = String(value.barcode);

      if (!groupByFieldValue) {
        return accumulator;
      }

      const oldGroupValue = accumulator[groupByFieldValue] || [];
      oldGroupValue.push(value.exam);
      accumulator[groupByFieldValue] = oldGroupValue;

      return accumulator;
    }, {});
  }

  removeExams(cardKey: string): void {
    const currentSelected = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];

    const updatedSelected = currentSelected.filter((selected: SelectedBarcodeExam) => selected.barcode !== cardKey);

    this.context.set({selectedBarcodeExams: updatedSelected});

    this.updateDeckFromContext();

    if(updatedSelected.length === 0) {
      ModalService.close(PendingExamsModalId)
    }
    this.showRemoveExamSuccessToast(cardKey);
  }

  onResetClick(evt: ClickEvent): void {
    const removedCardKey = evt.value?.cardKey;

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.form.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.removeExams(removedCardKey),
      title: 'tws.wizard.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'tws.selected-pending-exams.remove-exam-confirmation',
      dockButtons: buttons,
      focusOnOpen: true,
      onCancel: () => { },
      onSubmit: () => { },
      title: 'text.message-alert-attention',
      type: ModalType.DIALOG,
      uuid: generateUUID(),
    }

    ModalService.openDialog(dialogModal);
  }

  getGroupTitle(examList: ListPendingParams[]): string {
    if(examList && examList.length > 0){
      return examList[0].dsGrupoMatFrasco || '';
    }
    return '';
  }

  updateDeckFromContext(): void {
    const selectedBarcodeExams = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];

    const onResetClick = this.onResetClick;

    const groupedSelectedExams = this.groupData(selectedBarcodeExams);
    const keyMapper = (key: string): Record<string, Value> => ({
      cardKey: key,
      exams: groupedSelectedExams[key],
      onResetClick,
      title: this.getGroupTitle(groupedSelectedExams[key]),
    });
    const groupedSelectedExamsArr = Object.keys(groupedSelectedExams).map(keyMapper);

    this.deckApi.setData(groupedSelectedExamsArr);
  }

  @OnMount({ target: SelectedPendingExamsAliases.SELECTED_PENDING_EXAMS_DECK })
  onDeckMount(): void {
    this.updateDeckFromContext();
  }

  @OnClick({ target: SelectedPendingExamsAliases.BUTTON_ADD_SELECTED_PENDING_EXAMS })
  async onSubmit(): Promise<void> {
    const selectedBarcodeExams = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];
    const submitSelectedExamList = this.context.get('submitSelectedExamList') as submitSelectedExamListFn
    submitSelectedExamList(selectedBarcodeExams);
  }

  showErrorToast(errorMessage: string): void {
    const toastId = 'invalid-barcode-error-toast:' + errorMessage;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: ToastLevel.ERROR,
      message: MessageService.translate(errorMessage),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000
    });
  }

  showRemoveExamSuccessToast(title: string): void {
    const toastId = 'remove-exam-success-toast' + title;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: ToastLevel.SUCCESS,
      message: MessageService.translate('tws.selected-pending-exams.remove-exam-success'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000
    });
  }
}

export default SelectedPendingExamsController;
